<template>
  <div class="package-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.WholesalePriceList.line6_text') }}</h1>
      <div class="header-actions">
        <div class="header-icon" @click="createPackage">➕</div>
      </div>
    </header>

    <!-- 页签切换 -->
    <div class="tab-section">
      <div 
        :class="['tab-item-custom', viewMode === 'list' ? 'active' : '']" 
        @click="viewMode = 'list'"
      >
        {{ $t('wholesaler.WholesalePriceList.tabList') }}
      </div>
      <div 
        :class="['tab-item-custom', viewMode === 'matrix' ? 'active' : '']" 
        @click="viewMode = 'matrix'"
      >
        {{ $t('wholesaler.WholesalePriceList.tabMatrix') }}
      </div>
    </div>

    <!-- 批发报价列表页签内容 -->
    <div v-if="viewMode === 'list'" class="list-content">
      <!-- 筛选条件 -->
      <div class="filter-section">
        <div class="filter-item">
          <label class="filter-label">{{ $t('wholesaler.WholesalePriceList.line33_text') }}</label>
          <select v-model="filterProductId" class="filter-select" @change="loadPackages">
            <option value="">{{ $t('wholesaler.WholesalePriceList.line35_text') }}</option>
            <option v-for="product in productList" :key="product.id" :value="product.id">
              {{ product.name }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <label class="filter-label">{{ $t('wholesaler.WholesalePriceList.line42_text') }}</label>
          <select v-model="filterCustomerId" class="filter-select" @change="loadPackages">
            <option value="">{{ $t('wholesaler.WholesalePriceList.line44_text') }}</option>
            <option v-for="customer in customerList" :key="customer.id" :value="customer.id">
              {{ customer.customerName || customer.customerName }}
            </option>
          </select>
        </div>
        <div class="filter-item">
          <button class="filter-reset-btn" @click="resetFilters">{{ $t('wholesaler.WholesalePriceList.line51_text') }}</button>
        </div>
      </div>

      <!-- 统计卡片 -->
      <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-value">{{ stats.packages }}</div>
        <div class="stat-label">{{ $t('wholesaler.WholesalePriceList.line59_text') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.active }}</div>
        <div class="stat-label">{{ $t('wholesaler.WholesalePriceList.line63_text') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ stats.customers }}</div>
        <div class="stat-label">{{ $t('wholesaler.WholesalePriceList.line67_text') }}</div>
      </div>
    </div>

    <!-- 列表视图 -->
    <div v-if="viewMode === 'list'" class="package-list">
      <div v-for="pkg in filteredPackages" :key="pkg.id" class="package-item" :class="{ 'active': pkg.status === 'active', 'inactive': pkg.status === 'inactive' }">
        <div class="package-header">
          <div class="package-header-left">
            <div class="package-name">{{ pkg.packageName }}</div>
            <div class="package-validity-inline" v-if="pkg.validStartDate || pkg.validEndDate">
              <span class="validity-label">{{ $t('wholesaler.WholesalePriceList.line78_text') }}</span>
              <span class="validity-date">
                <span v-if="pkg.validStartDate">{{ formatDate(pkg.validStartDate) }}</span>
                <span v-if="pkg.validStartDate && pkg.validEndDate"> {{ $t('wholesaler.WholesalePriceList.line81_text') }} </span>
                <span v-if="pkg.validEndDate">{{ formatDate(pkg.validEndDate) }}</span>
                <span v-if="!pkg.validStartDate && !pkg.validEndDate">{{ $t('wholesaler.WholesalePriceList.line83_text') }}</span>
              </span>
            </div>
          </div>
          <span class="package-status" :class="pkg.status === 'active' ? 'status-active' : 'status-inactive'">
            {{ pkg.status === 'active' ? $t('wholesaler.WholesalePriceList.statusActive') : $t('wholesaler.WholesalePriceList.statusInactive') }}
          </span>
        </div>

        <div class="package-products" v-if="pkg.products && pkg.products.length > 0">
          <div class="section-title">{{ $t('wholesaler.WholesalePriceList.productsInQuote', { count: pkg.products.length }) }}</div>
          <div class="product-list">
            <span v-for="product in pkg.products.slice(0, 5)" :key="product.id" class="product-tag">
              {{ product.name }} <span class="price">¥{{ product.wholesalePrice }}</span>
            </span>
            <span v-if="pkg.products.length > 5" class="product-tag more">+{{ pkg.products.length - 5 }}{{ $t('wholesaler.WholesalePriceList.unit') }}</span>
          </div>
        </div>

        <div class="package-customers" v-if="pkg.customers && pkg.customers.length > 0">
          <div class="section-title">{{ $t('wholesaler.WholesalePriceList.publishedTo', { count: pkg.customers.length }) }}</div>
          <div class="customer-list">
            <span v-for="customer in pkg.customers.slice(0, 4)" :key="customer.id" class="customer-tag">
              🏪 {{ customer.name }}
            </span>
            <span v-if="pkg.customers.length > 4" class="customer-tag more">+{{ pkg.customers.length - 4 }}{{ $t('wholesaler.WholesalePriceList.unit') }}</span>
          </div>
        </div>

        <div class="package-actions">
          <button class="action-btn btn-secondary" @click="editPackage(pkg)">{{ $t('common.edit') }}</button>
          <button class="action-btn btn-secondary" @click="showQRCode(pkg)">{{ $t('wholesaler.WholesalePriceList.line114_text') }}</button>
          <button class="action-btn btn-secondary" @click="copyPackage(pkg)">{{ $t('wholesaler.WholesalePriceList.line115_text') }}</button>
          <button v-if="pkg.status === 'active'" class="action-btn btn-danger" @click="deactivatePackage(pkg)">{{ $t('wholesaler.WholesalePriceList.line116_text') }}</button>
          <button v-else class="action-btn btn-success" @click="activatePackage(pkg)">{{ $t('wholesaler.WholesalePriceList.line117_text') }}</button>
        </div>
      </div>

      <div v-if="packages.length === 0" class="empty-state">
        <div class="empty-icon">📦</div>
        <p class="empty-text">{{ $t('wholesaler.WholesalePriceList.line123_text') }}</p>
        <p v-if="filterProductId" class="empty-desc">{{ $t('wholesaler.WholesalePriceList.line124_text') }}</p>
        <p v-else class="empty-desc">{{ $t('wholesaler.WholesalePriceList.line125_text') }}</p>
        <button v-if="!filterProductId" @click="createPackage" class="btn-create">{{ $t('wholesaler.WholesalePriceList.line126_text') }}</button>
        <button v-else @click="goBack" class="btn-create">{{ $t('common.back') }}</button>
      </div>
    </div>
    </div>

    <!-- 价格矩阵页签内容 -->
    <div v-if="viewMode === 'matrix'" class="matrix-view">
      <div class="matrix-info">
        {{ $t('wholesaler.WholesalePriceList.matrixHint') }}
        <button class="matrix-export-btn" @click="exportMatrixToExcel">{{ $t('wholesaler.WholesalePriceList.line136_text') }}</button>
      </div>
      <div class="matrix-container">
        <table class="matrix-table">
          <thead>
            <tr>
              <th class="matrix-header-corner">{{ $t('wholesaler.WholesalePriceList.line142_text') }}</th>
              <th v-for="customer in matrixCustomers" :key="customer.id" class="matrix-header-customer">
                🏪 {{ customer.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in matrixProducts" :key="product.id">
              <td class="matrix-row-header">🏷️ {{ product.name }}</td>
              <td v-for="customer in matrixCustomers" :key="customer.id" class="matrix-cell">
                <div v-if="getMatrixPrice(product.id, customer.id)" class="cell-content">
                  <span class="cell-price">¥{{ getMatrixPrice(product.id, customer.id) }}</span>
                </div>
                <div v-else class="cell-empty">-</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="matrixProducts.length === 0 || matrixCustomers.length === 0" class="empty-matrix">
        <div class="empty-icon">📊</div>
        <p class="empty-text">{{ $t('wholesaler.WholesalePriceList.line163_text') }}</p>
        <p class="empty-desc">{{ $t('wholesaler.WholesalePriceList.line164_text') }}</p>
      </div>
    </div>

    <!-- 二维码弹窗 -->
    <div v-if="qrCodeVisible" class="qr-modal-overlay" @click="qrCodeVisible = false">
      <div class="qr-modal qr-modal-large" @click.stop>
        <div class="qr-modal-header">
          <div class="header-left">
            <h3>🏷️ {{ currentPackage && currentPackage.packageName }}</h3>
            <div class="package-status-tag" :class="currentPackage && currentPackage.status">
              {{ currentPackage && currentPackage.status === 'active' ? $t('wholesaler.WholesalePriceList.statusActive') : $t('wholesaler.WholesalePriceList.statusInactive') }}
            </div>
          </div>
          <button class="qr-close" @click="qrCodeVisible = false">×</button>
        </div>
        
        <div class="qr-modal-body">
          <div class="modal-content-grid">
            <!-- 左侧：二维码 -->
            <div class="qr-column">
              <div class="qr-preview-card">
                <div class="qr-preview-title">{{ $t('wholesaler.WholesalePriceList.line186_text') }}</div>
                <div class="qr-code-container" ref="qrCodeContainer"></div>
                <div class="qr-preview-hint">
                  <div>{{ $t('wholesaler.WholesalePriceList.line189_text') }}</div>
                  <div class="qr-url">{{ qrCodeUrl }}</div>
                </div>
              </div>
              
              <!-- 操作按钮 -->
              <div class="action-buttons">
                <button class="action-btn action-btn-primary" @click="showPrintMenu = true">
                  <span class="btn-icon">🖨️</span>
                  <span class="btn-text">{{ $t('wholesaler.WholesalePriceList.line198_text') }}</span>
                </button>
                <button class="action-btn" @click="showShareMenu = true">
                  <span class="btn-icon">📤</span>
                  <span class="btn-text">{{ $t('wholesaler.WholesalePriceList.line202_text') }}</span>
                </button>
                <button class="action-btn" @click="downloadQRCode">
                  <span class="btn-icon">⬇️</span>
                  <span class="btn-text">{{ $t('wholesaler.WholesalePriceList.line206_text') }}</span>
                </button>
              </div>
            </div>
            
            <!-- 右侧：信息卡片 -->
            <div class="info-column">
              <!-- 批发商信息 -->
              <div class="info-card-large">
                <div class="info-card-header">
                  <span class="card-icon">🏢</span>
                  <span class="card-title">{{ $t('wholesaler.WholesalePriceList.line217_text') }}</span>
                </div>
                <div class="info-card-content">
                  <div class="info-row">
                    <span class="info-row-label">{{ $t('wholesaler.WholesalePriceList.line221_text') }}</span>
                    <span class="info-row-value">{{ wholesalerInfo.companyName || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-row-label">{{ $t('wholesaler.WholesalePriceList.line225_text') }}</span>
                    <span class="info-row-value phone-number">{{ wholesalerInfo.contactPhone || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-row-label">{{ $t('wholesaler.WholesalePriceList.line229_text') }}</span>
                    <span class="info-row-value">{{ wholesalerInfo.address || '-' }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 商品信息 -->
              <div class="products-card-large">
                <div class="info-card-header">
                  <span class="card-icon">📦</span>
                  <span class="card-title">{{ $t('wholesaler.WholesalePriceList.productsCount', { count: (currentPackage && currentPackage.products) ? currentPackage.products.length : 0 }) }}</span>
                </div>
                <div class="products-table-large">
                  <table>
                    <thead>
                      <tr>
                        <th>{{ $t('wholesaler.WholesalePriceList.line245_text') }}</th>
                        <th class="text-center">{{ $t('wholesaler.WholesalePriceList.line246_text') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="product in (currentPackage && currentPackage.products) || []" :key="product.productId">
                        <td class="product-name-cell">{{ product.name }}</td>
                        <td class="text-center price-cell">¥{{ product.wholesalePrice }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <!-- 有效期 -->
              <div v-if="currentPackage && (currentPackage.validStartDate || currentPackage.validEndDate)" class="validity-card">
                <span class="validity-icon">📅</span>
                <span class="validity-text">
                  {{ $t('wholesaler.WholesalePriceList.validPeriodLabel') }}
                  <span v-if="currentPackage.validStartDate">{{ formatDate(currentPackage.validStartDate) }}</span>
                  <span v-if="currentPackage.validStartDate && currentPackage.validEndDate"> {{ $t('wholesaler.WholesalePriceList.line265_text') }} </span>
                  <span v-if="currentPackage.validEndDate">{{ formatDate(currentPackage.validEndDate) }}</span>
                  <span v-if="!currentPackage.validStartDate && !currentPackage.validEndDate">{{ $t('wholesaler.WholesalePriceList.line267_text') }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="qr-modal-footer-large">
          <div class="footer-hint">
            {{ $t('wholesaler.WholesalePriceList.printHint') }}
          </div>
        </div>
      </div>
    </div>

    <!-- 打印菜单弹窗 -->
    <div v-if="showPrintMenu" class="share-menu-overlay" @click="showPrintMenu = false">
      <div class="share-menu" @click.stop>
        <div class="share-menu-header">
          <h4>{{ $t('wholesaler.WholesalePriceList.line286_text') }}</h4>
          <button class="share-menu-close" @click="showPrintMenu = false">×</button>
        </div>
        <div class="share-menu-options">
          <div class="share-option" @click="generateImage('a4')">
            <div class="share-option-icon a4">📄</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line293_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line294_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="generateImage('a5')">
            <div class="share-option-icon a5">📋</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line300_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line301_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="generateImage('card')">
            <div class="share-option-icon card">💳</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line307_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line308_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="generateImage('sticker')">
            <div class="share-option-icon sticker">🏷️</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line314_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line315_text') }}</div>
            </div>
          </div>
        </div>
        <div class="print-tips">
          <div class="print-tips-title">{{ $t('wholesaler.WholesalePriceList.line320_text') }}</div>
          <div class="print-tips-list">
            <div class="print-tip">{{ $t('wholesaler.WholesalePriceList.line322_text') }}</div>
            <div class="print-tip">{{ $t('wholesaler.WholesalePriceList.line323_text') }}</div>
            <div class="print-tip">{{ $t('wholesaler.WholesalePriceList.line324_text') }}</div>
            <div class="print-tip">{{ $t('wholesaler.WholesalePriceList.line325_text') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分享菜单弹窗 -->
    <div v-if="showShareMenu" class="share-menu-overlay" @click="showShareMenu = false">
      <div class="share-menu" @click.stop>
        <div class="share-menu-header">
          <h4>{{ $t('wholesaler.WholesalePriceList.line335_text') }}</h4>
          <button class="share-menu-close" @click="showShareMenu = false">×</button>
        </div>
        <div class="share-menu-options">
          <div class="share-option" @click="shareToWeChat">
            <div class="share-option-icon wechat">💚</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line342_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line343_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="shareToWeChatMoments">
            <div class="share-option-icon moments">🟢</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line349_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line350_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="shareToWhatsApp">
            <div class="share-option-icon whatsapp">💬</div>
            <div class="share-option-text">
              <div class="share-option-title">WhatsApp</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line357_text') }}</div>
            </div>
          </div>
          <div class="share-option" @click="copyShareLink">
            <div class="share-option-icon copy">🔗</div>
            <div class="share-option-text">
              <div class="share-option-title">{{ $t('wholesaler.WholesalePriceList.line363_text') }}</div>
              <div class="share-option-desc">{{ $t('wholesaler.WholesalePriceList.line364_text') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="tab-bar">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item active">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.WholesalePriceList.line383_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getPackageList, copyPackage, deactivatePackage, activatePackage } from '@/api/wholesaler/package'
import api from '@/services/api'
import QRCode from 'qrcodejs2'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'WholesalePriceList',
  data() {
    return {
      stats: {
        packages: 0,
        active: 0,
        customers: 0
      },
      packages: [],
      loading: false,
      filterProductId: null,
      filterCustomerId: null,
      productList: [],
      customerList: [],
      minPriceMap: {}, // ⭐ 最低价矩阵 key=productId_customerId
      viewMode: 'list', // 'list' or 'matrix'
      // 二维码相关
      qrCodeVisible: false,
      currentPackage: null,
      qrCodeUrl: '',
      qrCodeContainer: null,
      // 分享菜单
      showShareMenu: false,
      // 打印菜单
      showPrintMenu: false,
      // 批发商信息
      wholesalerInfo: {
        companyName: '',
        contactPhone: '',
        address: '',
        username: ''
      }
    }
  },
  async mounted() {
    // 检查是否有商品筛选参数
    const productId = this.$route.query.productId
    if (productId) {
      this.filterProductId = parseInt(productId)
    }
    // 加载商品和档口列表
    await this.loadProductList()
    await this.loadCustomerList()
    // 加载批发报价列表
    this.loadPackages()
    // 加载批发商信息
    this.loadWholesalerInfo()
  },
  
  computed: {
    filteredPackages() {
      if (!this.packages || !Array.isArray(this.packages)) {
        return []
      }
      
      let result = [...this.packages]
      
      if (this.filterProductId) {
        const filterId = Number(this.filterProductId)
        result = result.filter(pkg => 
          pkg.products && pkg.products.some(p => Number(p.productId) === filterId)
        )
      }
      
      if (this.filterCustomerId) {
        const filterId = Number(this.filterCustomerId)
        result = result.filter(pkg => 
          pkg.customers && pkg.customers.some(c => Number(c.customerId) === filterId)
        )
      }
      
      return result
    },
    matrixProducts() {
      const productMap = new Map()
      this.packages.forEach(pkg => {
        if (pkg.status !== 'active' || !pkg.products) return
        pkg.products.forEach(p => {
          if (!productMap.has(p.productId)) {
            productMap.set(p.productId, { id: p.productId, name: p.name })
          }
        })
      })
      if (this.filterProductId) {
        return Array.from(productMap.values()).filter(p => p.id === this.filterProductId)
      }
      return Array.from(productMap.values())
    },
    matrixCustomers() {
      const customerMap = new Map()
      this.packages.forEach(pkg => {
        if (pkg.status !== 'active' || !pkg.customers) return
        pkg.customers.forEach(c => {
          if (!customerMap.has(c.customerId)) {
            customerMap.set(c.customerId, { id: c.customerId, name: c.name })
          }
        })
      })
      if (this.filterCustomerId) {
        return Array.from(customerMap.values()).filter(c => c.id === this.filterCustomerId)
      }
      return Array.from(customerMap.values())
    }
  },
  
  methods: {
    // 矩阵视图：获取商品对档口的批发价
    getMatrixPrice(productId, customerId) {
      // ⭐ 使用后端计算好的 minPriceMap
      const key = productId + '_' + customerId
      return this.minPriceMap?.[key] || null
    },
    async loadProductList() {
      try {
        // 使用 /product/list 接口获取所有商品
        const res = await api.get('/product/list', { params: { page: 0, size: 1000 } })
        if (res && res.data && res.data.content) {
          this.productList = res.data.content
        } else if (Array.isArray(res)) {
          this.productList = res
        }
      } catch (error) {
        console.error('加载商品列表失败:', error)
      }
    },
    async loadCustomerList() {
      try {
        // 使用 /customers 接口获取所有客户（需要 ADMIN 或 WHOLESALER 权限）
        const res = await api.get('/customers', { params: { page: 0, size: 1000 } })
        if (res && res.data && res.data.content) {
          this.customerList = res.data.content
        } else if (Array.isArray(res)) {
          this.customerList = res
        }
      } catch (error) {
        // 403/404 时静默处理，不影响其他功能
        console.warn('加载档口列表失败（可能是权限不足）:', error.message)
        this.customerList = []
      }
    },
    async loadPackages() {
      try {
        this.loading = true
        const res = await getPackageList()
        // api.js 返回响应数据
        // 后端格式可能是数组或 { code: 200, data: [...] }
        let packages = res
        if (res && res.data) {
          packages = res.data
        }
        // 确保是数组
        this.packages = Array.isArray(packages) ? packages : []
        
        // ⭐ 提取后端返回的 minPriceMap
        this.minPriceMap = {}
        if (this.packages.length > 0) {
          const firstPkg = this.packages[0]
          if (firstPkg && firstPkg.minPriceMap) {
            this.minPriceMap = firstPkg.minPriceMap
            console.log('📊 最低价矩阵已加载，共', Object.keys(this.minPriceMap).length, '条记录')
          }
        }
        
        // ⭐ 商品筛选
        if (this.filterProductId) {
          this.packages = this.packages.filter(pkg => 
            pkg.products && pkg.products.some(p => p.productId === this.filterProductId)
          )
          console.log('🔍 商品筛选后剩余', this.packages.length, '个批发报价')
        }
        
        // ⭐ 档口筛选
        if (this.filterCustomerId) {
          this.packages = this.packages.filter(pkg => 
            pkg.customers && pkg.customers.some(c => c.customerId === this.filterCustomerId)
          )
          console.log('🏪 档口筛选后剩余', this.packages.length, '个批发报价')
        }
        
        this.updateStats()
      } catch (error) {
        console.error('加载批发报价列表失败:', error)
        this.$message.error(error.message || '加载批发报价列表失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filterProductId = null
      this.filterCustomerId = null
      this.loadPackages()
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    updateStats() {
      this.stats.packages = this.packages.length
      this.stats.active = this.packages.filter(p => p.status === 'active').length
      // 统计覆盖档口数量（去重）
      const customerSet = new Set()
      this.packages.forEach(pkg => {
        if (pkg.customers) {
          pkg.customers.forEach(c => customerSet.add(c.customerId))
        }
      })
      this.stats.customers = customerSet.size
    },
    createPackage() {
      // 如果有商品筛选，跳转到创建页面并带入商品 ID
      if (this.filterProductId) {
        this.$router.push({
          path: '/wholesaler/create-package',
          query: { productIds: this.filterProductId }
        })
      } else {
        this.$router.push('/wholesaler/create-package')
      }
    },
    // 编辑批发报价
    editPackage(pkg) {
      console.log('编辑批发报价:', pkg.id)
      // 跳转到编辑页面，带入批发报价 ID
      this.$router.push({
        path: '/wholesaler/create-package',
        query: { packageId: pkg.id }
      })
    },
    // eslint-disable-next-line no-unused-vars
    async copyPackage(pkg) {
      try {
        await copyPackage(pkg.id)
        this.$message.success('复制成功')
        this.loadPackages()
      } catch (error) {
        this.$message.error(error.message || '复制失败')
      }
    },
    // eslint-disable-next-line no-unused-vars
    async deactivatePackage(pkg) {
      try {
        const confirmed = confirm('确定要失效该批发报价吗？')
        if (!confirmed) return
        
        await deactivatePackage(pkg.id)
        this.$message.success('失效成功')
        this.loadPackages()
      } catch (error) {
        console.error('失效失败:', error)
        this.$message.error(error.message || '失效失败')
      }
    },
    // eslint-disable-next-line no-unused-vars
    async activatePackage(pkg) {
      try {
        const confirmed = confirm('确定要重新生效该批发报价吗？')
        if (!confirmed) return
        
        await activatePackage(pkg.id)
        this.$message.success('重新生效成功')
        this.loadPackages()
      } catch (error) {
        console.error('重新生效失败:', error)
        this.$message.error(error.message || '重新生效失败')
      }
    },
    // 显示二维码
    showQRCode(pkg) {
      this.currentPackage = pkg
      // 生成档口预订链接
      const baseUrl = window.location.origin
      this.qrCodeUrl = pkg.qrCodeUrl
      this.qrCodeVisible = true
      
      // 重新加载批发商信息（确保最新）
      this.loadWholesalerInfo()
      
      // 延迟生成二维码（确保 DOM 已渲染）
      this.$nextTick(() => {
        this.generateQRCode()
      })
    },
    // 生成二维码
    generateQRCode() {
      const container = this.$refs.qrCodeContainer
      if (!container) return
      
      // 清空容器
      container.innerHTML = ''
      
      // 直接使用 import 的 QRCode
      new QRCode(container, {
        text: this.qrCodeUrl,
        width: 200,
        height: 200,
        colorDark: '#000000',
        colorLight: '#ffffff',
        correctLevel: QRCode.CorrectLevel.H
      })
    },
    // 下载二维码
    downloadQRCode() {
      const container = this.$refs.qrCodeContainer
      if (!container) return
      
      // 等待一下确保二维码已生成
      setTimeout(() => {
        const canvas = container.querySelector('canvas')
        const img = container.querySelector('img')
        
        if (canvas) {
          // 从 canvas 下载
          const packageName = this.currentPackage ? (this.currentPackage.packageName || this.currentPackage.id) : '批发报价'
          const link = document.createElement('a')
          link.download = `批发报价-${packageName}.png`
          link.href = canvas.toDataURL('image/png')
          link.click()
        } else if (img && img.src) {
          // 从 img 下载
          const packageName = this.currentPackage ? (this.currentPackage.packageName || this.currentPackage.id) : '批发报价'
          const link = document.createElement('a')
          link.download = `批发报价-${packageName}.png`
          link.href = img.src
          link.click()
        } else {
          this.$message.warning('二维码尚未生成，请稍后再试')
        }
      }, 100)
    },
    goToPage(path) {
      this.$router.push(path)
    },
    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      };
      if (routes[tab]) {
        this.$router.push(routes[tab]);
      }
    },
    goBack() {
      this.$router.back()
    },
    // 加载批发商信息
    async loadWholesalerInfo() {
      const wholesalerId = getWholesalerId()
      
      console.log('🔍 加载批发商信息')
      console.log('  - wholesalerId:', wholesalerId)
      
      if (wholesalerId) {
        try {
          console.log('🔍 调用 API: /wholesalers/' + wholesalerId)
          const res = await api.get(`/wholesalers/${wholesalerId}`)
          console.log('🔍 批发商详情响应:', res)
          console.log('🔍 res.companyName:', res.companyName)
          
          // api.js 已经解包，直接用 res 而不是 res.data
          if (res && res.companyName) {
            this.wholesalerInfo = {
              companyName: res.companyName || '未知批发商',
              contactPhone: res.contactPhone || '-',
              address: res.address || '-',
              username: res.contactPerson || ''
            }
            console.log('✅ 从 API 加载成功:', this.wholesalerInfo)
            return
          }
        } catch (apiError) {
          console.error('❌ API 调用失败:', apiError.message)
        }
      }
    },
    // 分享到微信朋友
    shareToWeChat() {
      this.copyShareLink()
      this.showShareMenu = false
      this.$message.success('链接已复制，请粘贴到微信聊天窗口')
    },
    // 分享到朋友圈
    shareToWeChatMoments() {
      this.copyShareLink()
      this.showShareMenu = false
      this.$message.success('链接已复制，请粘贴到朋友圈')
    },
    // 分享到 WhatsApp
    shareToWhatsApp() {
      const packageName = this.currentPackage ? this.currentPackage.packageName : '批发报价'
      const shareText = `${packageName} - ${this.wholesalerInfo.companyName || '批发商'}\n${this.qrCodeUrl}`
      const encodedText = encodeURIComponent(shareText)
      const whatsappUrl = `https://wa.me/?text=${encodedText}`
      
      window.open(whatsappUrl, '_blank')
      this.showShareMenu = false
    },
    // 复制分享链接
    copyShareLink() {
      const packageName = this.currentPackage ? this.currentPackage.packageName : '批发报价'
      const shareText = `${packageName}\n` +
        `${this.wholesalerInfo.companyName || '批发商'}\n` +
        `电话：${this.wholesalerInfo.contactPhone || '-'}\n` +
        `地址：${this.wholesalerInfo.address || '-'}\n` +
        `链接：${this.qrCodeUrl}`
      
      navigator.clipboard.writeText(shareText).then(() => {
        this.$message.success('链接已复制到剪贴板')
      }).catch(() => {
        this.$message.warning('复制失败，请手动复制链接')
      })
    },
    // 生成图片（带尺寸选择）
    generateImage(size = 'a4') {
      this.showPrintMenu = false
      
      const packageName = this.currentPackage ? this.currentPackage.packageName : '批发报价'
      const qrCodeImg = this.$refs.qrCodeContainer && this.$refs.qrCodeContainer.querySelector('img') 
        ? this.$refs.qrCodeContainer.querySelector('img').src 
        : ''
      
      // 尺寸配置（像素）
      const sizeConfig = {
        a4: { width: 1654, height: 2339, qrSize: 400, fontSize: 20, title: 'A4 尺寸' },
        a5: { width: 1169, height: 1654, qrSize: 300, fontSize: 16, title: 'A5 尺寸' },
        card: { width: 900, height: 540, qrSize: 120, fontSize: 14, title: '名片尺寸' },
        sticker: { width: 1000, height: 1000, qrSize: 200, fontSize: 16, title: '方形贴纸' }
      }
      
      const config = sizeConfig[size] || sizeConfig.a4
      
      // 创建 canvas
      const canvas = document.createElement('canvas')
      canvas.width = config.width
      canvas.height = config.height
      const ctx = canvas.getContext('2d')
      
      // 白色背景
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, config.width, config.height)
      
      let yPos = 40 // 当前 Y 坐标
      
      if (size === 'card') {
        // 名片样式
        // 渐变背景
        const gradient = ctx.createLinearGradient(0, 0, config.width, config.height)
        gradient.addColorStop(0, '#667eea')
        gradient.addColorStop(1, '#764ba2')
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, config.width, config.height)
        
        // 标题
        ctx.fillStyle = '#ffffff'
        ctx.font = `bold ${config.fontSize + 4}px "Microsoft YaHei"`
        ctx.textAlign = 'left'
        ctx.fillText(`🏷️ ${packageName}`, 40, 50)
        
        // 二维码
        if (qrCodeImg) {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.src = qrCodeImg
          img.onload = () => {
            ctx.drawImage(img, 40, 80, config.qrSize, config.qrSize)
            
            // 批发商信息
            ctx.fillStyle = '#ffffff'
            ctx.font = `bold 20px "Microsoft YaHei"`
            ctx.fillText(this.wholesalerInfo.companyName || '-', 200, 110)
            
            ctx.fillStyle = '#ffd700'
            ctx.font = `bold 28px "Microsoft YaHei"`
            ctx.fillText(`📞 ${this.wholesalerInfo.contactPhone || '-'}`, 200, 150)
            
            // 主打商品
            ctx.fillStyle = '#ffffff'
            ctx.font = `bold 16px "Microsoft YaHei"`
            ctx.fillText('🔥 主打商品：', 40, 210)
            
            if (this.currentPackage && this.currentPackage.products && this.currentPackage.products.length > 0) {
              ctx.font = `14px "Microsoft YaHei"`
              const topProducts = this.currentPackage.products.slice(0, 3)
              topProducts.forEach((product, index) => {
                ctx.fillStyle = '#f0f0f0'
                ctx.fillText(`${product.name} ¥${product.wholesalePrice}`, 60, 235 + (index * 25))
              })
            }
            
            // 下载
            this.downloadCanvas(canvas, `批发报价-${packageName}-${config.title}`)
          }
        }
        return
      }
      
      if (size === 'sticker') {
        // 方形贴纸样式
        // 标题
        ctx.fillStyle = '#667eea'
        ctx.font = `bold ${config.fontSize + 4}px "Microsoft YaHei"`
        ctx.textAlign = 'center'
        ctx.fillText('🔥 主打商品', config.width / 2, 50)
        
        // 二维码
        if (qrCodeImg) {
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.src = qrCodeImg
          img.onload = () => {
            // 带边框
            ctx.strokeStyle = '#667eea'
            ctx.lineWidth = 8
            ctx.strokeRect(
              (config.width - config.qrSize) / 2 - 8,
              80,
              config.qrSize + 16,
              config.qrSize + 16
            )
            ctx.drawImage(img, (config.width - config.qrSize) / 2, 80, config.qrSize, config.qrSize)
            
            // 电话
            ctx.fillStyle = '#ff4d4f'
            ctx.font = `bold 32px "Microsoft YaHei"`
            ctx.fillText(`📞 ${this.wholesalerInfo.contactPhone || '-'}`, config.width / 2, config.height - 200)
            
            // 公司名
            ctx.fillStyle = '#666666'
            ctx.font = `${config.fontSize}px "Microsoft YaHei"`
            ctx.fillText(this.wholesalerInfo.companyName || '-', config.width / 2, config.height - 160)
            
            // 主打商品
            if (this.currentPackage && this.currentPackage.products && this.currentPackage.products.length > 0) {
              ctx.fillStyle = '#333333'
              ctx.font = `bold 16px "Microsoft YaHei"`
              const topProducts = this.currentPackage.products.slice(0, 2)
              topProducts.forEach((product, index) => {
                ctx.fillText(`${product.name} ¥${product.wholesalePrice}`, config.width / 2, config.height - 110 + (index * 30))
              })
            }
            
            // 下载
            this.downloadCanvas(canvas, `批发报价-${packageName}-${config.title}`)
          }
        }
        return
      }
      
      // A4/A5 标准样式
      // 头部渐变
      const headerGradient = ctx.createLinearGradient(0, 0, config.width, 150)
      headerGradient.addColorStop(0, '#667eea')
      headerGradient.addColorStop(1, '#764ba2')
      ctx.fillStyle = headerGradient
      ctx.fillRect(0, 0, config.width, 150)
      
      // 标题
      ctx.fillStyle = '#ffffff'
      ctx.font = `bold ${config.fontSize + 10}px "Microsoft YaHei"`
      ctx.textAlign = 'center'
      ctx.fillText(`🏷️ ${packageName}`, config.width / 2, 60)
      ctx.font = `${config.fontSize}px "Microsoft YaHei"`
      ctx.fillText('扫码预订 享受批发优惠', config.width / 2, 100)
      
      yPos = 220
      
      // 二维码区域
      ctx.fillStyle = '#333333'
      ctx.font = `bold ${config.fontSize + 4}px "Microsoft YaHei"`
      ctx.fillText('📱 扫码立即预订', config.width / 2, yPos)
      yPos += 50
      
      if (qrCodeImg) {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.src = qrCodeImg
        img.onload = () => {
          // 紫色边框
          ctx.strokeStyle = '#667eea'
          ctx.lineWidth = 6
          ctx.strokeRect(
            (config.width - config.qrSize) / 2 - 8,
            yPos,
            config.qrSize + 16,
            config.qrSize + 16
          )
          ctx.drawImage(img, (config.width - config.qrSize) / 2, yPos, config.qrSize, config.qrSize)
          
          yPos += config.qrSize + 60
          
          // 批发商信息卡片
          ctx.fillStyle = headerGradient
          ctx.fillRect(100, yPos, config.width - 200, 180)
          
          ctx.fillStyle = '#ffffff'
          ctx.font = `bold ${config.fontSize + 2}px "Microsoft YaHei"`
          ctx.textAlign = 'left'
          ctx.fillText('🏢 批发商信息', 140, yPos + 40)
          
          ctx.font = `${config.fontSize}px "Microsoft YaHei"`
          ctx.fillText(`批发商：${this.wholesalerInfo.companyName || '-'}`, 140, yPos + 80)
          ctx.fillStyle = '#ff4d4f'
          ctx.font = `bold ${config.fontSize + 4}px "Microsoft YaHei"`
          ctx.fillText(`📞 电话：${this.wholesalerInfo.contactPhone || '-'}`, 140, yPos + 120)
          ctx.fillStyle = '#ffffff'
          ctx.font = `${config.fontSize}px "Microsoft YaHei"`
          ctx.fillText(`📍 地址：${this.wholesalerInfo.address || '-'}`, 140, yPos + 160)
          
          yPos += 220
          
          // 商品列表标题
          ctx.fillStyle = '#333333'
          ctx.font = `bold ${config.fontSize + 2}px "Microsoft YaHei"`
          ctx.textAlign = 'center'
          const productsTitle = `🔥 主打商品（共${this.currentPackage && this.currentPackage.products ? this.currentPackage.products.length : 0}个）`
          ctx.fillText(productsTitle, config.width / 2, yPos)
          yPos += 40
          
          // 商品表头
          const tableLeft = 100
          const colWidths = [config.width - 300, 200]
          ctx.fillStyle = '#667eea'
          ctx.fillRect(tableLeft, yPos, config.width - 200, 50)
          
          ctx.fillStyle = '#ffffff'
          ctx.font = `bold ${config.fontSize}px "Microsoft YaHei"`
          ctx.textAlign = 'left'
          ctx.fillText('商品名称', tableLeft + 20, yPos + 32)
          ctx.textAlign = 'center'
          ctx.fillText('批发价', tableLeft + colWidths[0] + 20, yPos + 32)
          
          yPos += 60
          
          // 商品列表
          if (this.currentPackage && this.currentPackage.products) {
            ctx.textAlign = 'left'
            this.currentPackage.products.forEach((product, index) => {
              if (yPos > config.height - 150) return // 防止超出
              
              const rowY = yPos + (index * 50)
              ctx.fillStyle = index % 2 === 0 ? '#fafafa' : '#ffffff'
              ctx.fillRect(tableLeft, rowY - 20, config.width - 200, 50)
              
              // 商品名称
              ctx.fillStyle = '#333333'
              ctx.font = `${config.fontSize}px "Microsoft YaHei"`
              ctx.fillText(product.name, tableLeft + 20, rowY + 5)
              
              // 批发价
              ctx.fillStyle = '#ff4d4f'
              ctx.font = `bold ${config.fontSize + 2}px "Microsoft YaHei"`
              ctx.textAlign = 'center'
              ctx.fillText(`¥${product.wholesalePrice}`, tableLeft + colWidths[0] + 20, rowY + 5)
            })
          }
          
          // 底部提示
          ctx.fillStyle = '#999999'
          ctx.font = `${config.fontSize - 2}px "Microsoft YaHei"`
          ctx.textAlign = 'center'
          ctx.fillText(`生成时间：${new Date().toLocaleString('zh-CN')}`, config.width / 2, config.height - 30)
          
          // 下载图片
          this.downloadCanvas(canvas, `批发报价-${packageName}-${config.title}`)
        }
      }
    },
    
    // 下载 Canvas 图片
    downloadCanvas(canvas, filename) {
      try {
        canvas.toBlob(blob => {
          if (!blob) {
            this.$message.error('图片生成失败')
            return
          }
          
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `${filename}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          
          this.$message.success('图片已保存到相册，可前往打印店打印')
        }, 'image/png', 0.95)
      } catch (error) {
        console.error('图片生成失败:', error)
        this.$message.error('图片生成失败，请重试')
      }
    },
    
    // 导出价格矩阵到 Excel
    exportMatrixToExcel() {
      if (this.matrixProducts.length === 0 || this.matrixCustomers.length === 0) {
        this.$message.warning('暂无数据可导出')
        return
      }
      
      // 构建 CSV 内容
      const rows = []
      
      // 表头：商品 \ 档口, 档口1, 档口2, ...
      const header = ['商品 \\ 档口']
      this.matrixCustomers.forEach(customer => {
        header.push(customer.name || '未知档口')
      })
      rows.push(header)
      
      // 数据行
      this.matrixProducts.forEach(product => {
        const row = [product.name || '未知商品']
        this.matrixCustomers.forEach(customer => {
          const price = this.getMatrixPrice(product.id, customer.id)
          row.push(price ? `¥${price}` : '-')
        })
        rows.push(row)
      })
      
      // 转换为 CSV 字符串（UTF-8 with BOM）
      const csvContent = '\uFEFF' + rows.map(row => 
        row.map(cell => {
          const str = String(cell || '')
          // 如果包含逗号、引号或换行符，用引号包裹
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`
          }
          return str
        }).join(',')
      ).join('\n')
      
      // 创建 Blob 并下载
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().slice(0, 10)
      link.download = `价格矩阵_${timestamp}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      this.$message.success('价格矩阵已下载')
    },
  }
}
</script>

<style scoped>
.package-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

.back-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: calc(16px + env(safe-area-inset-top, 0px));
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  font-size: 20px;
  cursor: pointer;
}

.filter-section {
  background: white;
  padding: 12px;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  border-bottom: 1px solid #e8e8e8;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  min-width: 120px;
  background: white;
  cursor: pointer;
}

.filter-select:focus {
  border-color: #667eea;
  outline: none;
}

.filter-reset-btn {
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  font-weight: 500;
}

.filter-reset-btn:hover {
  background: #e8e8e8;
}

.back-header h1 {
  font-size: 17px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.filter-info {
  font-size: 13px;
  background: rgba(255,255,255,0.2);
  padding: 4px 10px;
  border-radius: 12px;
  margin-right: 8px;
}

.header-icon {
  font-size: 20px;
  cursor: pointer;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e8e8e8;
}

.stat-card {
  text-align: center;
  padding: 8px;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #fa8c16;
}

.stat-label {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.package-list {
  padding: 12px 16px;
}

.package-item {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 8px;
  border: 1px solid #e8e8e8;
}

.package-item.inactive {
  border-left-color: #999;
  opacity: 0.8;
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.package-header-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.package-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.package-validity-inline {
  font-size: 12px;
  color: #999;
}

.validity-label {
  color: #666;
}

.validity-date {
  color: #999;
}

.package-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #f6ffed;
  color: #52c41a;
}

.status-inactive {
  background: #f5f5f5;
  color: #999;
}

.package-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
}

.info-grid-item {
  text-align: center;
}

.info-grid-value {
  font-size: 18px;
  font-weight: 700;
  color: #667eea;
}

.info-grid-label {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.package-products {
  margin-bottom: 12px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
}

.product-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.product-tag {
  background: #f0f0f0;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.product-tag .price {
  color: #ff4d4f;
  font-weight: 600;
}

.product-tag.more {
  background: #f5f5f5;
  color: #999;
}

.package-customers {
  margin-bottom: 12px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
}

.customer-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.customer-tag {
  background: #e6f4ff;
  color: #1890ff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
}

.customer-tag.more {
  background: #f0f0f0;
  color: #666;
}

.package-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
}

.btn-danger {
  background: #fff2f0;
  color: #ff4d4f;
}

.btn-success {
  background: #f6ffed;
  color: #52c41a;
}

/* 二维码弹窗 */
.qr-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.qr-modal {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 320px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.qr-modal-large {
  max-width: 900px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}

.qr-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 2px solid #e8e8e8;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.qr-modal-large .qr-modal-header {
  padding: 24px 32px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.qr-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
}

.package-status-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.package-status-tag.active {
  background: rgba(82, 196, 26, 0.9);
}

.package-status-tag.inactive {
  background: rgba(153, 153, 153, 0.9);
}

.qr-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-close:hover {
  color: #333;
}

.qr-modal-body {
  padding: 0;
}

.modal-content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0;
}

.qr-column {
  padding: 24px;
  background: #f9f9f9;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.qr-preview-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.qr-preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 16px;
}

.qr-code-container {
  display: inline-block;
  padding: 16px;
  background: white;
  border: 3px solid #667eea;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.qr-preview-hint {
  font-size: 13px;
  color: #666;
  margin-top: 12px;
}

.qr-preview-hint .qr-url {
  font-size: 11px;
  color: #999;
  margin-top: 6px;
  word-break: break-all;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn .btn-icon {
  font-size: 18px;
}

.action-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.action-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.action-btn {
  background: white;
  color: #666;
  border: 2px solid #e8e8e8;
}

.action-btn:hover {
  background: #f5f5f5;
  border-color: #d9d9d9;
}

.qr-desc {
  font-size: 13px;
  color: #666;
  margin: 8px 0;
}

.qr-url {
  font-size: 11px;
  color: #999;
  word-break: break-all;
  margin: 0;
}

/* 批发商信息区域 */
.wholesaler-info-section {
  margin: 16px 0;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: left;
}

.info-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.info-item {
  display: flex;
  margin: 8px 0;
  font-size: 13px;
  line-height: 1.5;
}

.info-label {
  color: #666;
  font-weight: 500;
  min-width: 80px;
  flex-shrink: 0;
}

.info-value {
  color: #333;
  word-break: break-word;
}

/* 商品信息区域 */
.products-info-section {
  margin: 16px 0;
  padding: 12px;
  background: #fff5f5;
  border-radius: 8px;
  text-align: left;
  max-height: 200px;
  overflow-y: auto;
}

.product-list-detail {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-item-detail {
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #ffe5e5;
}

.product-name-detail {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.price-detail {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.market-price-detail {
  font-size: 12px;
  color: #999;
  text-decoration: line-through;
}

.wholesale-price-detail {
  font-size: 13px;
  color: #ff4d4f;
  font-weight: 600;
}

.discount-detail {
  font-size: 11px;
  color: #52c41a;
  background: #f6ffed;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

/* 分享链接区域 */
.share-url-section {
  margin-top: 12px;
  padding: 8px;
  background: #fafafa;
  border-radius: 6px;
}

.share-url-section .qr-url {
  font-size: 11px;
  color: #666;
}

/* 二维码弹窗底部按钮 */
.qr-modal-footer {
  padding: 16px;
  border-top: 1px solid #e8e8e8;
  text-align: center;
  display: flex;
  gap: 8px;
  justify-content: center;
}

.btn-secondary-qr {
  padding: 10px 16px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  max-width: 100px;
}

.btn-secondary-qr:hover {
  background: #e8e8e8;
}

.btn-download-qr {
  padding: 10px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  max-width: 120px;
}

.btn-download-qr:hover {
  opacity: 0.9;
}

/* 分享菜单 */
.share-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1001;
}

.share-menu {
  background: white;
  border-radius: 16px 16px 0 0;
  width: 100%;
  max-width: 500px;
  padding: 0;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.share-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e8e8e8;
}

.share-menu-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.share-menu-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-menu-close:hover {
  color: #333;
}

.share-menu-options {
  padding: 12px;
}

.share-option {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
  margin-bottom: 8px;
}

.share-option:last-child {
  margin-bottom: 0;
}

.share-option:hover {
  background: #f5f5f5;
}

.share-option-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-right: 16px;
  flex-shrink: 0;
}

.share-option-icon.wechat {
  background: #07c160;
  color: white;
}

.share-option-icon.moments {
  background: #07c160;
  color: white;
}

.share-option-icon.whatsapp {
  background: #25d366;
  color: white;
}

.share-option-icon.copy {
  background: #1890ff;
  color: white;
}

.share-option-text {
  flex: 1;
}

.share-option-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.share-option-desc {
  font-size: 13px;
  color: #999;
}

/* 打印菜单特殊样式 */
.share-option-icon.a4 {
  background: #1890ff;
  color: white;
}

.share-option-icon.a5 {
  background: #52c41a;
  color: white;
}

.share-option-icon.card {
  background: #fa8c16;
  color: white;
}

.share-option-icon.sticker {
  background: #722ed1;
  color: white;
}

.print-tips {
  margin: 16px;
  padding: 16px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
}

.print-tips-title {
  font-size: 14px;
  font-weight: 600;
  color: #d46b08;
  margin-bottom: 12px;
}

.print-tips-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.print-tip {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.print-tip strong {
  color: #333;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #999;
  margin-bottom: 20px;
}

.btn-create {
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  display: flex;
  border-top: 1px solid #e8e8e8;
  padding-bottom: env(safe-area-inset-bottom);
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

/* 页签切换 */
.tab-section {
  display: flex;
  align-items: center;
  background: white;
  border-bottom: 1px solid #e8e8e8;
  padding: 8px 16px;
  gap: 16px;
}

.tab-item-custom {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.tab-item-custom:hover {
  background: #f5f5f5;
}

.tab-item-custom.active {
  background: #e6f4ff;
  color: #1890ff;
}

.tab-info {
  margin-left: auto;
  font-size: 12px;
  color: #999;
}

/* 列表内容区域 */
.list-content {
  padding-bottom: 80px;
}

/* 有效期样式 */
.package-validity {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f9f9f9;
  border-radius: 8px;
}

.validity-text {
  font-size: 13px;
  color: #666;
}

/* 矩阵视图样式 */
.matrix-view {
  padding: 12px;
  position: relative;
}

.matrix-info {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #0050b3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.matrix-export-btn {
  margin-left: auto;
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.matrix-export-btn:hover {
  opacity: 0.9;
}

.matrix-container {
  background: white;
  border-radius: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  -webkit-overflow-scrolling: touch;
}

.empty-matrix {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  margin-top: 12px;
}

.empty-matrix .empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-matrix .empty-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.empty-matrix .empty-desc {
  font-size: 14px;
  color: #999;
}

.matrix-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.matrix-table th,
.matrix-table td {
  padding: 12px;
  text-align: center;
  border: 1px solid #e8e8e8;
  min-width: 100px;
}

.matrix-table th {
  background: #fafafa;
  font-weight: 600;
  color: #333;
}

.matrix-header-corner {
  background: #f0f0f0;
  min-width: 120px;
  text-align: left;
}

.matrix-header-customer {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.matrix-row-header {
  background: #fafafa;
  font-weight: 500;
  text-align: left;
  min-width: 120px;
}

.matrix-cell {
  background: white;
}

.cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.cell-price {
  color: #ff4d4f;
  font-weight: 700;
  font-size: 14px;
}

.cell-empty {
  color: #d9d9d9;
  font-size: 18px;
}

.matrix-table tr:hover td {
  background: #f5f5f5;
}

/* ========== 二维码弹窗（大尺寸优化版）========== */
.qr-modal-large {
  max-width: 900px;
  width: 95%;
  max-height: 90vh;
  overflow-y: auto;
}

.qr-modal-large .qr-modal-header {
  padding: 24px 32px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.qr-modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
}

.package-status-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.package-status-tag.active {
  background: rgba(82, 196, 26, 0.9);
}

.package-status-tag.inactive {
  background: rgba(153, 153, 153, 0.9);
}

.qr-modal-body {
  padding: 0;
}

.modal-content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0;
}

.qr-column {
  padding: 24px;
  background: #f9f9f9;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.qr-preview-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.qr-preview-title {
  font-size: 16px;
  font-weight: 600;
  color: #667eea;
  margin-bottom: 16px;
}

.qr-code-container {
  display: inline-block;
  padding: 16px;
  background: white;
  border: 3px solid #667eea;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.qr-preview-hint {
  font-size: 13px;
  color: #666;
  margin-top: 12px;
}

.qr-preview-hint .qr-url {
  font-size: 11px;
  color: #999;
  margin-top: 6px;
  word-break: break-all;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn .btn-icon {
  font-size: 18px;
}

.action-btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.action-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.action-btn {
  background: white;
  color: #666;
  border: 2px solid #e8e8e8;
}

.action-btn:hover {
  background: #f5f5f5;
  border-color: #d9d9d9;
}

.info-column {
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: white;
}

.info-card-large {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.15);
  font-size: 16px;
  font-weight: 700;
}

.card-icon {
  font-size: 20px;
}

.card-title {
  flex: 1;
}

.info-card-content {
  padding: 20px;
  background: white;
}

.info-row {
  display: flex;
  margin: 14px 0;
  font-size: 15px;
  align-items: baseline;
}

.info-row-label {
  width: 100px;
  color: #666;
  font-weight: 600;
  flex-shrink: 0;
}

.info-row-value {
  flex: 1;
  color: #333;
  font-weight: 600;
  word-break: break-word;
}

.info-row-value.phone-number {
  color: #ff4d4f;
  font-size: 18px;
  font-weight: 700;
}

.products-card-large {
  background: white;
  border-radius: 12px;
  border: 2px solid #e8e8e8;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.products-table-large {
  max-height: 280px;
  overflow-y: auto;
}

.products-table-large table {
  width: 100%;
  border-collapse: collapse;
}

.products-table-large th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px 12px;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  position: sticky;
  top: 0;
  z-index: 1;
}

.products-table-large th.text-center {
  text-align: center;
}

.products-table-large td {
  padding: 14px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}

.products-table-large tr:last-child td {
  border-bottom: none;
}

.products-table-large tr:nth-child(even) {
  background: #fafafa;
}

.products-table-large tr:hover {
  background: #f5f5f5;
}

.product-name-cell {
  font-weight: 500;
  color: #333;
}

.price-cell {
  color: #ff4d4f;
  font-weight: 700;
  font-size: 15px;
  text-align: center;
}

.discount-cell {
  color: #52c41a;
  font-weight: 600;
  background: #f6ffed;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  text-align: center;
}

.text-center {
  text-align: center;
}

.validity-card {
  background: #fff7e6;
  border: 2px solid #ffd591;
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #d46b08;
  box-shadow: 0 2px 6px rgba(255, 213, 145, 0.3);
}

.validity-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.validity-text {
  font-weight: 600;
  word-break: break-word;
}

.qr-modal-footer-large {
  padding: 16px 24px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
}

.footer-hint {
  font-size: 13px;
  color: #666;
  text-align: center;
}

/* 响应式 */
@media (max-width: 768px) {
  .modal-content-grid {
    grid-template-columns: 1fr;
  }
  
  .qr-column {
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
  }
}
</style>
